import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent as CardBody, CardTitle } from '@shadcn/components/ui/card';
import { Row } from '@shadcn/components/ui/row';
import { Col } from '@shadcn/components/ui/col';
import useQuery from '../../../helpers/JIFI/useQuery';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import useSKUStatus from '../helpers/useSKUStatus';
import { groupBy } from './utils/groupBy';
import Controls from './components/Controls';
import ActiveFilters from './components/ActiveFilters';
import StockTable from './components/StockTable';
import moment from 'moment';
import { generateCsvFromVariants } from './utils/csvExporter';
import { REGION_CODES } from '../constants/regions';

const EXCLUDED_TOTAL_STOCK_LOCATIONS = new Set(['Highpoint', 'Emporium']);

const mergeLocationOrder = (locationOrder = {}, locationsByRegion = {}) => {
  const result = {};
  const regions = new Set([
    ...Object.keys(locationOrder || {}),
    ...Object.keys(locationsByRegion || {}),
  ]);

  regions.forEach((region) => {
    const configuredLocations = (locationOrder?.[region] || [])
      .filter((location) => !EXCLUDED_TOTAL_STOCK_LOCATIONS.has(location));
    const activeLocations = (locationsByRegion?.[region] || [])
      .map((location) => location.name)
      .filter((location) => !EXCLUDED_TOTAL_STOCK_LOCATIONS.has(location));
    result[region] = [...new Set([...configuredLocations, ...activeLocations])];
  });

  return result;
};

const AllStockNew = () => {
  const [expandedProducts, setExpandedProducts] = useState([]);
  const [tableData, setTableData] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState({
    ...Object.fromEntries(REGION_CODES.map(region => [region, false])),
  });

  const [filters, setFilters] = useState({
    selectedRegions: [],
    selectedLocations: ['HQ2', 'QV Store'],
    searchTerm: '',
    statusFilter: [],
    hasContainer: false,
    containerDateFrom: '',
    containerDateTo: '',
    hasPending: false,
    pendingMin: '',
    pendingMax: '',
    skus: [],
    productNames: [],
  });

  const findSKUStatus = useSKUStatus();

  const dateString = new Date().toDateString().replace(/ /g, '-');
  const [locationsByRegion, regionState] = useQuery({
    url: '/inventory/getLocationRegion',
  });

  const [locationOrder, locationOrderState] = useQuery({
    url: '/locationOrderConfig/totalStockOrder',
  });

  const effectiveLocationOrder = useMemo(
    () => mergeLocationOrder(locationOrder, locationsByRegion),
    [locationOrder, locationsByRegion]
  );

  const [data, state] = useQuery({
    url: '/product/all-stock-v2-with-filter',
    queryData: {
      locations: filters.selectedLocations,
      skus: filters.skus,
    },
  });

  const [containers] = useQuery({
    url: '/inventory/fetchUpcomingContainerTotalStock/',
  });

  const [pendingData] = useQuery({ url: '/inventory/pendingStock' });
  const [pendingDataUS] = useQuery({ url: '/inventory/pendingStockUS' });
  const [pendingDataUK] = useQuery({ url: '/inventory/pendingStockUK' });

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const toggleProductExpansion = useCallback((productName) => {
    setExpandedProducts((prev) =>
      prev.includes(productName)
        ? prev.filter((name) => name !== productName)
        : [...prev, productName]
    );
  }, []);

  useEffect(() => {
    if (locationsByRegion && filters.selectedLocations.length > 0) {
      const regionsWithSelectedLocations = Object.keys(locationsByRegion).filter(
        (region) => {
          const regionLocationNames =
            locationsByRegion[region]?.map((loc) => loc.name) || [];
          return filters.selectedLocations.some((loc) =>
            regionLocationNames.includes(loc)
          );
        }
      );

      if (
        JSON.stringify(regionsWithSelectedLocations.sort()) !==
        JSON.stringify(filters.selectedRegions.sort())
      ) {
        setFilters((prev) => ({
          ...prev,
          selectedRegions: regionsWithSelectedLocations,
        }));
      }
    }
  }, [locationsByRegion, filters.selectedLocations]);


  const getLocationDisplayName = useCallback((locationName) => {
    if (!locationsByRegion) return locationName;
    

    const customMappings = {
      'EWE': 'EWE - Melbourne',
      'CHDRETAIL': 'Chadstone',
      'Galeries Sydney': 'Galeries Store',
      'LCHSLC': 'UTAH',
      'LCHKTC': 'KENTUCKY',
      'NYC Greene St': 'GREEEN ST'
    };
    
    if (customMappings[locationName]) {
      return customMappings[locationName];
    }
    
    
    for (const region of Object.values(locationsByRegion)) {
      const location = region.find(loc => loc.name === locationName);
      if (location?.display_name) {
        return location.display_name;
      }
    }
    
    
    return locationName;
  }, [locationsByRegion]);

  const toggleRegionExpansion = useCallback((region) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [region]: !prev[region],
    }));
  }, []);

  const locationsBySelectedRegion = useMemo(() => {
    if (!locationsByRegion || !filters.selectedLocations.length) return {};

    const result = {};
    const locationToRegion = {};

    Object.entries(locationsByRegion).forEach(([region, locations]) => {
      locations.forEach((location) => {
        locationToRegion[location.name] = region;
      });
    });

    filters.selectedLocations.forEach((locationName) => {
      if (EXCLUDED_TOTAL_STOCK_LOCATIONS.has(locationName)) return;
      const region = locationToRegion[locationName];
      if (region) {
        if (!result[region]) {
          result[region] = [];
        }
        result[region].push(locationName);
      }
    });

    Object.keys(result).forEach((region) => {
      if (effectiveLocationOrder[region]) {
        result[region] = result[region].sort((a, b) => {
          const indexA = effectiveLocationOrder[region].indexOf(a);
          const indexB = effectiveLocationOrder[region].indexOf(b);
          
          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          
          return indexA - indexB;
        });
      }
    });

    return result;
  }, [locationsByRegion, filters.selectedLocations, effectiveLocationOrder]);

  useEffect(() => {
    if (containers && data?.variants && locationsByRegion) {
      const toUseContainers = containers || [];
      const updatedTable = {};

      const locationToRegion = {};
      Object.entries(locationsByRegion).forEach(([region, locations]) => {
        locations.forEach((location) => {
          locationToRegion[location.name] = region;
        });
      });

      data.variants.forEach((variant) => {
        const sku = variant.sku;

        let containerLineData = [...toUseContainers]
          .reverse()
          .map((container) => {
            let containerLine = container.containerLines?.find(
              (containerLine) => containerLine?.sku === sku
            );
            let count = containerLine == undefined ? 0 : containerLine.quantity;

            let location = container.landingLocation?.name;
            let date = container.arrivalDate;
            let region = locationToRegion[location] || 'UNKNOWN';

            return {
              count,
              location,
              date,
              region,
            };
          })
          .filter((val) => val.count !== 0);

        let auList = containerLineData.filter((val) => val.region === 'AU');
        let usList = containerLineData.filter((val) => val.region === 'US');
        let ukList = containerLineData.filter((val) => val.region === 'UK');

        updatedTable[sku] = {
          auNextContainer: auList.length > 0 ? auList[0] : null,
          usNextContainer: usList.length > 0 ? usList[0] : null,
          ukNextContainer: ukList.length > 0 ? ukList[0] : null,
        };
      });

      setTableData(updatedTable);
    }
  }, [containers, data, locationsByRegion]);

  const processedData = useMemo(() => {
    if (!data?.variants) return {};

    let filteredVariants = data.variants;
    

    if (filters.searchTerm) {
      filteredVariants = filteredVariants.filter(
        (variant) =>
          variant.sku?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          variant.variantName?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          variant.productName?.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }


    if (filters.statusFilter.length > 0) {
      filteredVariants = filteredVariants.filter((variant) => {
        const regionsWithSelectedLocations = Object.keys(locationsBySelectedRegion);
        return regionsWithSelectedLocations.some((region) => {
          const status = findSKUStatus(variant.sku, region);
          return filters.statusFilter.includes(status?.toLowerCase());
        });
      });
    }

    if (filters.hasContainer) {
      filteredVariants = filteredVariants.filter((variant) => {
        const container = tableData[variant.sku];
        if (!container) return false;

        const regionsWithSelectedLocations = Object.keys(locationsBySelectedRegion);
        return regionsWithSelectedLocations.some((region) => {
          const regionKey = region.toLowerCase() + 'NextContainer';
          const nextContainer = container[regionKey];

          if (!nextContainer?.date) return false;

          if (filters.containerDateFrom || filters.containerDateTo) {
            const containerDate = moment(nextContainer.date);
            if (
              filters.containerDateFrom &&
              containerDate.isBefore(moment(filters.containerDateFrom))
            )
              return false;
            if (
              filters.containerDateTo &&
              containerDate.isAfter(moment(filters.containerDateTo))
            )
              return false;
          }

          return true;
        });
      });
    }

    if (filters.hasPending) {
      filteredVariants = filteredVariants.filter((variant) => {
        const regionsWithSelectedLocations = Object.keys(locationsBySelectedRegion);
        return regionsWithSelectedLocations.some((region) => {
          let pending = 0;
          if (region === 'AU' && pendingData) {
            pending = pendingData[variant.sku] || 0;
          } else if (region === 'US' && pendingDataUS) {
            pending = pendingDataUS[variant.sku] || 0;
          } else if (region === 'UK' && pendingDataUK) {
            pending = pendingDataUK[variant.sku] || 0;
          }

          if (filters.pendingMin && pending < parseInt(filters.pendingMin))
            return false;
          if (filters.pendingMax && pending > parseInt(filters.pendingMax))
            return false;

          return pending > 0;
        });
      });
    }

    const groupedByProduct = groupBy(filteredVariants, (variant) => variant.productName);

    const productsWithVariants = Object.entries(groupedByProduct).map(
      ([productName, variants]) => ({
        productName,
        category: variants[0]?.category,
        variants: variants.sort((a, b) => a.sku?.localeCompare(b.sku)),
      })
    );

    return groupBy(productsWithVariants, (product) => product.category);
  }, [
    data,
    filters,
    tableData,
    findSKUStatus,
    pendingData,
    pendingDataUS,
    pendingDataUK,
    locationsBySelectedRegion,
  ]);

  const toggleCategoryExpansion = useCallback(
    (category) => {
      const categoryProducts = processedData[category];
      if (!categoryProducts) return;

      const categoryProductNames = categoryProducts.map(
        (product) => product.productName
      );
      const allExpanded = categoryProductNames.every((name) =>
        expandedProducts.includes(name)
      );

      if (allExpanded) {
        setExpandedProducts((prev) =>
          prev.filter((name) => !categoryProductNames.includes(name))
        );
      } else {
        setExpandedProducts((prev) => {
          const newExpanded = [...prev];
          categoryProductNames.forEach((name) => {
            if (!newExpanded.includes(name)) {
              newExpanded.push(name);
            }
          });
          return newExpanded;
        });
      }
    },
    [processedData, expandedProducts]
  );

  const extractVariantsFromProcessedData = useCallback(() => {
    const variants = [];

    
    const categories = [
      'CLASSIC',
      'LIGHT',
      'TRUNK',
      'SET',
      'BAG',
      'ACCESSORIES',
      'MISC',
    ];

    
    categories.forEach(category => {
      const categoryProducts = processedData[category];
      if (categoryProducts && Array.isArray(categoryProducts)) {
      categoryProducts.forEach(product => {
        if (product.variants && Array.isArray(product.variants)) {
          variants.push(...product.variants);
        }
      });
    }
    });

    return variants;
  }, [processedData]);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      
      const displayedVariants = extractVariantsFromProcessedData();

      if (displayedVariants.length === 0) {
        alert('No data to export. Please adjust your filters.');
        return;
      }

      const csvString = generateCsvFromVariants(
        displayedVariants,
        locationsBySelectedRegion,
        pendingData,
        pendingDataUS,
        pendingDataUK,
        containers,
        findSKUStatus,
        effectiveLocationOrder,
        getLocationDisplayName
      );

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `all-stock-displayed-data-${dateString}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('An error occurred while exporting CSV');
    } finally {
      setIsExporting(false);
    }
  }, [extractVariantsFromProcessedData, locationsBySelectedRegion, pendingData, pendingDataUS, pendingDataUK, containers, findSKUStatus, effectiveLocationOrder, getLocationDisplayName, dateString]);

  const toggleAllProducts = useCallback(() => {
    setExpandedProducts((prev) =>
      prev.length > 0
        ? []
        : Object.keys(processedData).flatMap((cat) =>
            processedData[cat]?.map((p) => p.productName) || []
          )
    );
  }, [processedData]);

  const clearFilters = useCallback(() => {
    if (!locationsByRegion) return;
    
    const allLocations = Object.values(locationsByRegion)
      .flat()
      .map(l => l.name)
      .filter(location => !EXCLUDED_TOTAL_STOCK_LOCATIONS.has(location));
    setFilters({
      selectedRegions: Object.keys(locationsByRegion),
      selectedLocations: allLocations,
      searchTerm: '',
      statusFilter: [],
      hasContainer: false,
      containerDateFrom: '',
      containerDateTo: '',
      hasPending: false,
      pendingMin: '',
      pendingMax: '',
    });
  }, [locationsByRegion]);

  const csvData = useMemo(() => {
    if (!data?.variants || !locationsBySelectedRegion) return [];

    const regionHeaders = [];
    Object.keys(locationsBySelectedRegion).forEach((region) => {
      regionHeaders.push(region, `${region} Status`, `${region} Container`);
    });
    const headers = ['Product/SKU', 'Variant Name', ...regionHeaders, 'TOTAL'];

    const rows = [];

    Object.keys(processedData).forEach((category) => {
      const categoryProducts = processedData[category];
      if (!categoryProducts) return;

      rows.push([`📦 ${category}`, '', ...regionHeaders.map(() => ''), '']);

      categoryProducts.forEach((product) => {
        const productRegionData = [];
        Object.entries(locationsBySelectedRegion).forEach(([region, locations]) => {
          const regionTotal = product.variants?.reduce(
            (sum, variant) =>
              sum +
              locations.reduce(
                (locSum, location) =>
                  locSum + (variant.locationCounts?.[location] || 0),
                0
              ),
            0
          ) || 0;
          productRegionData.push(regionTotal, '', '');
        });

        const productGrandTotal = Object.values(locationsBySelectedRegion).reduce(
          (sum, locations) =>
            sum +
            (product.variants?.reduce(
              (variantSum, variant) =>
                variantSum +
                locations.reduce(
                  (locSum, location) =>
                    locSum + (variant.locationCounts?.[location] || 0),
                  0
                ),
              0
            ) || 0),
          0
        );

        rows.push([product.productName, '', ...productRegionData, productGrandTotal]);

        product.variants?.forEach((variant) => {
          const skuRegionData = [];
          Object.entries(locationsBySelectedRegion).forEach(([region, locations]) => {
            const regionTotal = locations.reduce(
              (sum, location) => sum + (variant.locationCounts?.[location] || 0),
              0
            );
            const status = findSKUStatus(variant.sku, region) || 'Unknown';
            const container =
              tableData[variant.sku]?.[region.toLowerCase() + 'NextContainer']?.date
                ? moment(
                    tableData[variant.sku][region.toLowerCase() + 'NextContainer'].date
                  ).format('DD-MMM-YYYY')
                : '';
            skuRegionData.push(regionTotal, status, container);
          });

          const skuGrandTotal = Object.values(locationsBySelectedRegion).reduce(
            (sum, locations) =>
              sum +
              locations.reduce(
                (locSum, location) =>
                  locSum + (variant.locationCounts?.[location] || 0),
                0
              ),
            0
          );

          rows.push([`${variant.sku}`, variant.variantName || '', ...skuRegionData, skuGrandTotal]);
        });
      });
    });

    return [headers, ...rows];
  }, [data, locationsBySelectedRegion, processedData, findSKUStatus, tableData]);

  const isInitialLoading =
    state.loading || regionState.loading || locationOrderState.loading || !data || !locationsByRegion || !locationOrder;

  if (isInitialLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Inventory" breadcrumbItem="All Stock New" />
          <div className="flex justify-center items-center h-64">
            <div className="spinner-border text-primary m-1" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Inventory" breadcrumbItem="All Stock New" />

        <Row className="bg-white rounded-t-lg">
          <Col className="p-4">
            <Controls
              expandedProducts={expandedProducts}
              toggleAllProducts={toggleAllProducts}
              isFilterModalOpen={isFilterModalOpen}
              setIsFilterModalOpen={setIsFilterModalOpen}
              locationsByRegion={locationsByRegion}
              filters={filters}
              handleFiltersChange={handleFiltersChange}
              handleExportCsv={handleExportCsv}
              isExporting={isExporting}
              locationOrder={effectiveLocationOrder}
            />
            <ActiveFilters
              filters={filters}
              locationsByRegion={locationsByRegion}
              clearFilters={clearFilters}
            />
           
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="mb-6 rounded-none border-0">
              <CardBody className="p-2">
                <CardTitle className="text-[15px] mb-4">
                  Stock Levels by Selected Locations
                </CardTitle>
                <StockTable
                  processedData={processedData}
                  locationsBySelectedRegion={locationsBySelectedRegion}
                  expandedRegions={expandedRegions}
                  toggleRegionExpansion={toggleRegionExpansion}
                  expandedProducts={expandedProducts}
                  toggleProductExpansion={toggleProductExpansion}
                  toggleCategoryExpansion={toggleCategoryExpansion}
                  findSKUStatus={findSKUStatus}
                  tableData={tableData}
                  pendingData={pendingData}
                  pendingDataUS={pendingDataUS}
                  pendingDataUK={pendingDataUK}
                  getLocationDisplayName={getLocationDisplayName}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AllStockNew;
